export const consultantsData = {
  industry: "Consultants",
  slug: "consultants",
  
  // SEO Meta Tags
  seo: {
    title: "AI Intake Forms for Consultants | HeyIntake",
    description: "AI-powered intake forms for consultants. Qualify prospects, scope projects accurately, and identify decision-makers before wasting time on discovery calls.",
    keywords: [
      "consulting client intake form",
      "consultant lead qualification",
      "pre-consultation screening",
      "consulting project scoping",
      "client onboarding for consultants",
      "consulting CRM intake"
    ]
  },

  heroTitle: "Client Intake Forms for Consulting Professionals",
  heroSubtitle: "Qualify prospects, scope projects accurately, and win more of the right clients.",
  heroDescription: "From management consulting to IT advisory, HeyIntake helps you understand client needs before the first call and identify which opportunities are worth pursuing.",
  
  // NEW: Why AI Scoring section
  whyAIScoring: {
    title: "Why Consultants Need Smarter Lead Qualification",
    intro: "As a consultant, your time is your most valuable asset. Every hour spent on unqualified prospects is an hour not spent on billable work.",
    paragraphs: [
      "The traditional consulting sales process is broken. You spend hours on discovery calls with prospects who have champagne tastes and beer budgets. You write detailed proposals for companies that were never going to hire you. You engage with stakeholders who can't actually approve the project.",
      "HeyIntake's AI-powered intake forms flip this dynamic. Before you invest any time, you'll know the prospect's budget range, their authority to make decisions, and whether their timeline is realistic. Our adaptive scoring evaluates each inquiry based on factors that predict successful engagements.",
      "For management consultants, IT advisors, strategy consultants, and independent practitioners, this means spending more time on high-probability opportunities and less time on dead ends. The result: higher close rates, better-fit clients, and more profitable engagements."
    ]
  },
  
  painPoints: [
    {
      title: "Scope Creep Starts Early",
      description: "Projects go off-track because initial requirements weren't captured clearly enough upfront."
    },
    {
      title: "Wasted Discovery Calls",
      description: "Spending hours on calls with prospects who have unrealistic budgets or timelines."
    },
    {
      title: "Inconsistent Project Scoping",
      description: "Different team members gather different information, leading to inconsistent proposals."
    },
    {
      title: "Can't Identify Decision Makers",
      description: "Engaging with stakeholders who don't have authority to approve the project."
    },
    {
      title: "Missing Business Context",
      description: "Starting projects without fully understanding the client's industry, challenges, or goals."
    },
    {
      title: "No Lead Prioritization",
      description: "Treating all inquiries equally instead of focusing on high-value opportunities."
    }
  ],

  exampleFields: [
    {
      category: "Company Information",
      fields: [
        "Company name and industry",
        "Company size (employees/revenue)",
        "Your role and decision-making authority",
        "Key stakeholders involved",
        "Company website"
      ]
    },
    {
      category: "Project Requirements",
      fields: [
        "What challenge are you trying to solve?",
        "What does success look like?",
        "Previous attempts to solve this problem",
        "Expected deliverables",
        "Preferred engagement model"
      ]
    },
    {
      category: "Timeline & Budget",
      fields: [
        "Ideal project start date",
        "Project deadline or milestones",
        "Approved budget range",
        "Budget approval status"
      ]
    },
    {
      category: "Additional Context",
      fields: [
        "How did you hear about us?",
        "Have you worked with consultants before?",
        "What would make this engagement successful?",
        "Any concerns or constraints?"
      ]
    }
  ],

  // Example intake form section
  exampleIntakeForm: {
    title: "Example Consulting Intake Form",
    description: "Here's what an effective consulting inquiry form looks like:",
    fields: [
      { label: "What type of consulting do you need?", type: "select", options: ["Strategy", "Operations", "Marketing", "Technology", "HR/Organizational", "Financial", "Other"] },
      { label: "Describe your challenge or goal", type: "textarea" },
      { label: "Company size", type: "select", options: ["1-10 employees", "11-50", "51-200", "201-1000", "1000+"] },
      { label: "Project timeline", type: "select", options: ["ASAP", "1-3 months", "3-6 months", "6+ months", "Ongoing retainer"] },
      { label: "Budget range for this engagement", type: "select", options: ["$5K-$15K", "$15K-$50K", "$50K-$100K", "$100K+"] },
      { label: "Have you worked with consultants before?", type: "select", options: ["No, first time", "Yes, good experience", "Yes, mixed results"] },
      { label: "Decision timeline", type: "select", options: ["Ready now", "Within 2 weeks", "Within 1 month", "Just exploring"] }
    ],
    cta: "Use this exact form → Start for $1"
  },

  // NEW: How It Works section
  howItWorks: {
    title: "How HeyIntake Works for Consultants",
    subtitle: "From inquiry to qualified prospect in three simple steps",
    steps: [
      {
        title: "Prospects Complete Intake",
        description: "Potential clients share their project needs, budget, timeline, and decision-making authority upfront."
      },
      {
        title: "AI Evaluates Fit",
        description: "Our AI scores each inquiry based on budget alignment, authority, and project clarity."
      },
      {
        title: "Prioritize Best Opportunities",
        description: "Focus your proposal efforts on high-scoring prospects most likely to convert."
      }
    ]
  },

  aiScoringExplanation: {
    title: "AI-Powered Lead Qualification for Consultants",
    description: "HeyIntake's AI evaluates each inquiry based on factors that predict project success and profitability, helping you focus on the right opportunities.",
    factors: [
      "Budget alignment with typical project costs",
      "Decision-maker involvement and authority",
      "Clarity of problem statement and success criteria",
      "Realistic timeline expectations",
      "Company size and industry fit"
    ],
    exampleLead: {
      name: "David Chen, VP Operations",
      context: "Tech Company • 200 employees • Process Optimization",
      summary: "Clear problem (scaling operations), budget approved ($75K), decision maker engaged, 3-month timeline. Excellent fit for our methodology. Prioritize.",
      score: 89
    }
  },

  useCases: [
    {
      icon: "brain",
      title: "Score Leads by Project Fit",
      description: "AI identifies which inquiries match your ideal client profile and engagement size."
    },
    {
      icon: "chart",
      title: "Scope Projects Accurately",
      description: "Collect detailed requirements upfront to create accurate proposals and avoid scope creep."
    },
    {
      icon: "users",
      title: "Identify Decision Makers",
      description: "Know who has budget authority before investing time in lengthy sales cycles."
    },
    {
      icon: "zap",
      title: "Respond Faster to Hot Leads",
      description: "Get alerts when high-scoring prospects submit inquiries so you can follow up immediately."
    }
  ],

  // Cross-linking to related industries
  relatedIndustries: [
    { name: "Intake Forms for Lawyers", path: "/intake-form-for-lawyers" },
    { name: "Intake Forms for Coaches", path: "/intake-form-for-coaches" },
    { name: "Intake Forms for Agencies", path: "/intake-form-for-agencies" }
  ],

  ctaText: "Ready to Win More of the Right Clients?"
};
